<template>
  <div
    v-if="showing == 'web_awesome' && icon"
    id="code-snippet-web_awesome"
    role="tabpanel"
    aria-labelledby="tab-web_awesome"
  >
    <owa-code-block
      add-highlighting
      allow-shadow-copy
      copy-text="Copy Code Snippet"
      custom-copy-icon="faClone"
      copy-tracking-event="icon_web_awesome_code_copy_button_click"
      :copy-tracking-params="{ icon_name: icon.name, icon_style: styling.style, icon_family: styling.family }"
      lang="markup"
      :code="codeSnippet"
    />

    <icon-code-snippet-links
      :links="allWebAwesomeLinks"
      :selected-style="styling.style"
      :major-version="majorVersion"
    />
  </div>
</template>

<script>
  import trimStart from 'lodash/trimStart'
  import IconCodeSnippetLinks from './IconCodeSnippetLinks'
  import { webAwesomeLearnAboutLinks, WEB_AWESOME_ICON_DOCS_URL } from 'app/views/icons/routing.js'
  import { transformIconColoring } from './utils.js'
  import { removeOpacity } from 'app/views/search/colorUtils'

  // Font Awesome to Web Awesome style mappings
  const SIZE_MAP = {
    '2xs': '0.625em',
    'xs': '0.75em',
    'sm': '0.875em',
    'lg': '1.25em',
    'xl': '1.5em',
    '2xl': '2em',
    '1x': '1em',
    '2x': '2em',
    '3x': '3em',
    '4x': '4em',
    '5x': '5em',
    '6x': '6em',
    '7x': '7em',
    '8x': '8em',
    '9x': '9em',
    '10x': '10em'
  }

  // Web Awesome flip attribute values (app uses horizontal/vertical/both)
  const FLIP_MAP = {
    horizontal: 'x',
    vertical: 'y',
    both: 'both'
  }

  /**
   * Maps Font Awesome rotation/flip values to Web Awesome rotate or flip attribute string.
   * @param {string|null|undefined} rotation - e.g. 'rotate-90', 'flip-horizontal', '45'
   * @returns {string} Attribute fragment like ' rotate="90"' or ' flip="x"' or ''
   */
  function rotationToWebAwesomeAttribute(rotation) {
    if (!rotation) return ''
    const hasCustomRotation = Number.isInteger(parseInt(rotation, 10))
    if (rotation.includes('rotate')) {
      const parts = rotation.split('-')
      return ` rotate="${parts[1]}"`
    }
    if (hasCustomRotation) return ` rotate="${rotation}"`
    if (rotation.includes('flip')) {
      const parts = rotation.split('-')
      const flipValue = FLIP_MAP[parts[1]]
      return flipValue ? ` flip="${flipValue}"` : ''
    }
    return ''
  }

  export default {
    name: 'IconCodeSnippetWebAwesome',

    components: {
      IconCodeSnippetLinks
    },

    inject: {
      isWideCustomIcon: {
        default: () => false
      }
    },

    props: {
      defaultStyles: {
        type: Object
      },
      icon: {
        type: Object
      },
      isDuotone: {
        type: Boolean
      },
      showing: {
        required: false
      },
      styling: {
        type: Object
      },
      majorVersion: {
        type: Number
      }
    },

    data() {
      return {}
    },

    computed: {
      codeSnippet() {
        return `<wa-icon name="${this.icon.name}"${this.webAwesomeFamilyAndVariant}${this.translatedAnimation}${this.translatedRotation}${this.translatedIconStyles}></wa-icon>`
      },

      allWebAwesomeLinks() {
        const baseLinks = [
          {
            label: 'Adding Icons',
            path: WEB_AWESOME_ICON_DOCS_URL,
            external: true
          }
        ]

        return webAwesomeLearnAboutLinks({
          links: baseLinks,
          isDuotone: this.isDuotone,
          hasCustomSize: this.hasCustomSize,
          hasCustomColor: this.hasCustomColor,
          hasRotationOrFlip: !!this.styling?.rotation,
          hasAnimation: !!this.styling?.animation,
          isWideCustomIcon: this.isWideCustomIcon()
        })
      },

      webAwesomeFamilyAndVariant() {
        const { family, style } = this.styling

        // classic + solid: omit both (defaults)
        if (family === 'classic' && style === 'solid') {
          return ''
        } else if (family === 'classic') {
          // classic + other variant: omit family, show variant
          return ` variant="${style}"`
        } else if (family === 'brands') {
          // brands: show family only, no variant
          return ` family="${family}"`
        } else {
          // other families: show both
          return ` family="${family}" variant="${style}"`
        }
      },

      hasCustomSize() {
        return !!this.styling?.size && this.styling.size !== this.defaultStyles.size
      },

      hasCustomColor() {
        const { colorPrimary, colorSecondary, opacityPrimary, opacitySecondary } = this.styling
        const defaults = this.defaultStyles

        return (
          colorPrimary !== defaults.colorPrimary ||
          colorSecondary !== defaults.colorSecondary ||
          opacityPrimary !== defaults.opacityPrimary ||
          opacitySecondary !== defaults.opacitySecondary
        )
      },

      hasCustomRotation() {
        return Number.isInteger(parseInt(this.styling?.rotation))
      },

      // map Font Awesome size classes to font-size values
      fontSize() {
        if (!this.styling?.size) {
          return ''
        } else {
          return SIZE_MAP[this.styling.size] || ''
        }
      },

      translatedRotation() {
        return rotationToWebAwesomeAttribute(this.styling?.rotation)
      },

      translatedAnimation() {
        if (!this.styling?.animation) {
          return ''
        } else if (this.styling.animation === 'pulse') {
          // For v5 the app uses "pulse"; v6+ use "spin-pulse". Web Awesome expects "spin-pulse" for both.
          return ` animation="spin-pulse"`
        } else {
          return ` animation="${this.styling.animation}"`
        }
      },

      translatedSize() {
        if (!this.hasCustomSize || !this.fontSize) {
          return ''
        } else {
          return `font-size: ${this.fontSize};`
        }
      },

      // Web Awesome uses --primary-color, --secondary-color, etc.
      colorPrimary() {
        if (this.isDuotone && this.styling?.colorPrimary !== this.defaultStyles.colorPrimary) {
          return ` --primary-color: ${removeOpacity(this.styling.colorPrimary)};`
        } else if (this.styling?.colorPrimary !== this.defaultStyles.colorPrimary) {
          return ` color: ${removeOpacity(this.styling.colorPrimary)};`
        } else {
          return ''
        }
      },

      colorSecondary() {
        if (!this.isDuotone) {
          return ''
        } else if (this.styling?.colorSecondary !== this.defaultStyles.colorSecondary) {
          return ` --secondary-color: ${removeOpacity(this.styling.colorSecondary)};`
        } else {
          return ''
        }
      },

      opacityPrimary() {
        if (!this.isDuotone) {
          return ''
        } else if (this.styling?.opacityPrimary !== this.defaultStyles.opacityPrimary) {
          return ` --primary-opacity: ${this.styling.opacityPrimary};`
        } else {
          return ''
        }
      },

      opacitySecondary() {
        if (!this.isDuotone) {
          return ''
        } else if (this.styling?.opacitySecondary !== this.defaultStyles.opacitySecondary) {
          return ` --secondary-opacity: ${this.styling.opacitySecondary};`
        } else {
          return ''
        }
      },

      translatedIconStyles() {
        const colorString = transformIconColoring(
          this.styling,
          this.defaultStyles,
          this.colorPrimary,
          this.colorSecondary,
          this.opacityPrimary,
          this.opacitySecondary
        )
        const styles = [this.translatedSize, colorString].filter(Boolean)

        return styles.length > 0 ? ` style="${trimStart(styles.join(' '))}"` : ''
      }
    }
  }
</script>
